import './assets/scss/DefaultTheme.scss';
import React, { Component, Suspense } from 'react';
import { BrowserRouter, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import Loadable from 'react-loadable';
import { routes } from './routes';
import { withAuth0 } from '@auth0/auth0-react';
import { loginUserSuccess } from './redux/actions';
import API from './helpers/shared/api';
import { CookieWrapper } from './helpers/shared-variables';
import PropTypes from 'prop-types';
import NonAuthLayout from './components/NonAuthLayout';
import Logout from './pages/Logout';

// Lazy loading and code splitting -
// Derieved idea from https://blog.logrocket.com/lazy-loading-components-in-react-16-6-6cea535c0b52
const loading = () => <div></div>;

const AuthLayout = Loadable( {
	loader: () => import( './components/AuthLayout' ),
	render( loaded, props ) {
		let Component = loaded.default;
		return <Component {...props} />;
	},
	loading,
} );

/**
 * Exports the component with layout wrapped to it
 * @param {} WrappedComponent
 */
const withLayout = ( WrappedComponent ) => {
	const HOC = class extends Component {
		displayName() {
			return 'HOC';
		}

		render() {
			return <WrappedComponent {...this.props} />;
		}
	};

	return connect()( HOC );
};

/**
 * Main app component
 */
class App extends Component {
	constructor( props ) {
		super( props );

		this.state = {
			currentUser: null,
		};
	}

	static propTypes = {
		auth0: PropTypes.object,
		loginUserSuccess: PropTypes.func,
		user: PropTypes.object,
	}

	getLayout = ( route ) => {
		if ( route.name === 'Logout' ) {
			return Logout;
		}

		if ( this.state.currentUser ) {
			return AuthLayout;
		} else {
			return NonAuthLayout;
		}
	}

	componentDidUpdate( prevProps ) {
		const { auth0 } = this.props;

		if ( prevProps.auth0.isAuthenticated === false && auth0.isAuthenticated === true ) {
			auth0.getAccessTokenSilently( {
				authorizationParams: {
					audience: process.env.REACT_APP_AUTH0_AUDIENCE,
				},
			} ).then( access_token => {
				API.request( `users/auth?auth0_id=${this.props.auth0.user.sub}`, { headers: { bearer: access_token } } ).then( res => {
					if ( res.status === 200 ) {
						const user = res.user;
						CookieWrapper.set( 'user', user, { maxAge: 86400, domain: process.env.REACT_APP_COOKIE_DOMAIN } );
						CookieWrapper.set( 'token', access_token, { maxAge: 86400, domain: process.env.REACT_APP_COOKIE_DOMAIN } );
						this.setState( { currentUser: user } );
						this.props.loginUserSuccess( user );
					} else {
						// user not found
						auth0.logout();
					}
				} );
			} ).catch( () => {
				// access or refresh token error.
				// solution: remove cookies, clear localstorage, and sign user out
				CookieWrapper.remove( 'user', { domain: process.env.REACT_APP_COOKIE_DOMAIN } );
				CookieWrapper.remove( 'token', { domain: process.env.REACT_APP_COOKIE_DOMAIN } );
				window.localStorage.clear();
				auth0.logout();
			} );
		}
	}

	render() {
		const { isLoading, isAuthenticated, loginWithRedirect } = this.props.auth0;

		if ( isLoading ) {
			return '';
		}

		if ( !isAuthenticated ) {
			loginWithRedirect( {
				authorizationParams: {
					redirect_uri: window.location.origin,
				},
			} );
		}

		if ( !this.state.currentUser ) {
			return '';
		}

		return (
			// rendering the router with layout
			<BrowserRouter>
				<Switch>
					{routes.map( ( route ) => {
						return (
							<route.route
								key={route.path || route.name}
								path={route.path}
								exact={route.exact}
								roles={route.roles}
								component={withLayout( props => {
									let AuthComponent = this.getLayout( route );

									return (
										<Suspense fallback={loading()}>
											<AuthComponent {...props} currentUser={this.state.currentUser}>
												<route.component {...props} key={Date.now()} />
											</AuthComponent>
										</Suspense>
									);
								} )}
							/>
						);
					} )}
				</Switch>
			</BrowserRouter>
		);
	}
}

const mapStateToProps = ( state ) => {
	return {
		// The user isn't explicitly referenced, but it's needed for the redirect to work.
		user: state.Auth.user,
	};
};

export default withAuth0( connect( mapStateToProps, { loginUserSuccess } )( App ) );
